import React, { useState } from 'react';
import { Button } from 'react-bootstrap';

import { LomavisAspectRatioBadge } from '@/lomavisSharedUi/atoms/badges/LomavisAspectRatioBadge';
import { LomavisLazyImage } from '@/lomavisSharedUi/atoms/images/LomavisLazyImage';
import { LomavisOverlayToolTip1 } from '@/lomavisSharedUi/atoms/overlays/lomavisOverlayToolTip1/lomavisOverlayToolTip1';
import {
  OverlayTemplateCardLabels,
  OverlayTemplateSummary
} from '@/lomavisSharedUi/types/overlayTemplates';

export interface LomavisOverlayTemplateCardProps {
  template: OverlayTemplateSummary;
  labels: OverlayTemplateCardLabels;
  showAspectRatioOnPreview?: boolean;
  onEdit?: (templateId: string) => void;
  onChangeFolder?: (templateId: string) => void;
  onDuplicate?: (templateId: string) => void;
  onClone?: (templateId: string) => void;
  onDelete?: (templateId: string) => void;
  onStartPost?: (templateId: string) => void;
}

/**
 * Molecule: a reusable overlay-template preview card.
 *
 * Available actions are determined only by the callbacks supplied by the
 * parent, which keeps account, profile-group, and picker behavior outside the
 * presentation component.
 */
export const LomavisOverlayTemplateCard: React.FC<
  LomavisOverlayTemplateCardProps
> = ({
  template,
  labels,
  showAspectRatioOnPreview = false,
  onEdit,
  onChangeFolder,
  onDuplicate,
  onClone,
  onDelete,
  onStartPost
}) => {
  const [showActions, setShowActions] = useState(false);
  const invoke = (callback?: (templateId: string) => void) => () =>
    callback?.(template.id);
  const hasActions = Boolean(
    onEdit ||
      onChangeFolder ||
      onDuplicate ||
      onClone ||
      onDelete ||
      onStartPost
  );
  const hasManagementActions = Boolean(
    onEdit || onChangeFolder || onDuplicate || onClone || onDelete
  );

  return (
    <article
      className="card h-100 border bg-white overflow-hidden"
      tabIndex={hasActions ? 0 : undefined}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
      onClick={() => setShowActions(true)}
      onFocusCapture={() => setShowActions(true)}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          setShowActions(false);
        }
      }}
    >
      <div className="ratio ratio-1x1 bg-light position-relative">
        <div className="position-absolute top-0 start-0 w-100 h-100">
          <LomavisLazyImage
            src={template.thumbnailUrl}
            alt={template.title}
            loadingLabel={labels.thumbnailLoading}
            className="object-fit-cover"
            fallback={
              <div className="d-flex flex-column w-100 h-100 align-items-center justify-content-center gap-2 bg-light text-muted p-4 text-center">
                <i className="fa-regular fa-image fs-1" aria-hidden="true" />
                <span>{labels.thumbnailUnavailable}</span>
              </div>
            }
          />
        </div>

        {showAspectRatioOnPreview && (
          <div className="position-absolute top-0 start-0 p-2">
            <LomavisAspectRatioBadge value={template.aspectRatio} />
          </div>
        )}

        {hasActions && (
          <div
            className={`position-absolute top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center gap-2 bg-light bg-opacity-50 p-2 ${
              showActions ? 'opacity-100 pe-auto' : 'opacity-0 pe-none'
            }`}
          >
            <div className="d-flex flex-column align-items-stretch gap-2">
              {onStartPost && (
                <Button
                  type="button"
                  variant="primary"
                  className="w-100"
                  onClick={invoke(onStartPost)}
                >
                  {labels.startPost}
                </Button>
              )}

              {hasManagementActions && (
                <div className="d-flex align-items-center justify-content-center gap-2">
                  {onEdit && (
                    <LomavisOverlayToolTip1 text={labels.edit} placement="top">
                      <Button
                        type="button"
                        size="sm"
                        variant="light"
                        className="btn-icon"
                        aria-label={labels.edit}
                        onClick={invoke(onEdit)}
                      >
                        <i className="fa-regular fa-pen" aria-hidden="true" />
                      </Button>
                    </LomavisOverlayToolTip1>
                  )}

                  {onDuplicate && (
                    <LomavisOverlayToolTip1
                      text={labels.duplicate}
                      placement="top"
                    >
                      <Button
                        type="button"
                        size="sm"
                        variant="light"
                        className="btn-icon"
                        aria-label={labels.duplicate}
                        onClick={invoke(onDuplicate)}
                      >
                        <i className="fa-regular fa-copy" aria-hidden="true" />
                      </Button>
                    </LomavisOverlayToolTip1>
                  )}

                  {onClone && (
                    <LomavisOverlayToolTip1 text={labels.clone} placement="top">
                      <Button
                        type="button"
                        size="sm"
                        variant="light"
                        className="btn-icon"
                        aria-label={labels.clone}
                        onClick={invoke(onClone)}
                      >
                        <i className="fa-regular fa-clone" aria-hidden="true" />
                      </Button>
                    </LomavisOverlayToolTip1>
                  )}

                  {onChangeFolder && (
                    <LomavisOverlayToolTip1
                      text={labels.changeFolder}
                      placement="top"
                    >
                      <Button
                        type="button"
                        size="sm"
                        variant="light"
                        className="btn-icon"
                        aria-label={labels.changeFolder}
                        onClick={invoke(onChangeFolder)}
                      >
                        <i
                          className="fa-regular fa-folder-open"
                          aria-hidden="true"
                        />
                      </Button>
                    </LomavisOverlayToolTip1>
                  )}

                  {onDelete && (
                    <LomavisOverlayToolTip1
                      text={labels.delete}
                      placement="top"
                    >
                      <Button
                        type="button"
                        size="sm"
                        variant="danger"
                        className="btn-icon"
                        aria-label={labels.delete}
                        onClick={invoke(onDelete)}
                      >
                        <i className="fa-regular fa-trash" aria-hidden="true" />
                      </Button>
                    </LomavisOverlayToolTip1>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="card-body p-3">
        <h3 className="fs-7 fw-bold text-dark mb-1 text-truncate">
          {template.title}
        </h3>
        {!showAspectRatioOnPreview && (
          <LomavisAspectRatioBadge
            value={template.aspectRatio}
            className="px-2 py-1"
          />
        )}
      </div>
    </article>
  );
};
